import { weekdayName } from 'helpers/weekday';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './ResultsMatchTable.module.css';
import type { MatchOffer } from 'types/MatchOffer';
import type { ResultsLeagueBlock } from 'types/ResultsLeagueBlock';
import { LeagueContext } from 'context/leagueContext';
import { SPORT_NAMES_MAP } from 'config/constants';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';
import { ResultsFilterBar } from 'components/ResultsFilterBar/ResultsFilterBar';
import { sportTranslationKey } from 'helpers/sportI18n';
import { formatDateTime } from 'helpers/dateHelper';
import {
  buildResultsRowDisplay,
  filterResultsBlocks,
  localDayKey,
  NO_RESULTS_FILTERS,
  ResultsFilters,
} from 'helpers/resultsDisplayHelper';

export type { ResultsLeagueBlock } from 'types/ResultsLeagueBlock';

type TabKey = 'today' | 'yesterday';

function startOfLocalDay(base: Date): Date {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function isUnixInRange(sec: number, fromMs: number, toMs: number): boolean {
  const ms = sec * 1000;
  return ms >= fromMs && ms <= toMs;
}

function formatResultsDayHeading(d: Date, locale: string): string {
  const weekday = weekdayName(d, locale);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${weekday}, ${day}.${month}.`;
}

function groupMatchesByLocalDay(
  matches: MatchOffer[],
  locale: string
): { dayKey: string; label: string; matches: MatchOffer[] }[] {
  const map = new Map<string, { label: string; matches: MatchOffer[] }>();
  const sorted = [...matches].sort((a, b) => a.time - b.time);
  for (const m of sorted) {
    const d = new Date(m.time * 1000);
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const label = formatResultsDayHeading(d, locale);
    const cur = map.get(dayKey);
    if (cur) {
      cur.matches.push(m);
    } else {
      map.set(dayKey, { label, matches: [m] });
    }
  }
  return Array.from(map.entries()).map(([dayKey, v]) => ({
    dayKey,
    label: v.label,
    matches: v.matches,
  }));
}

type Cluster = {
  sportId: number;
  groupLabel: string | null;
  blocks: ResultsLeagueBlock[];
};

function clusterBySportAndGroup(blocks: ResultsLeagueBlock[]): Cluster[] {
  const sorted = [...blocks].sort((a, b) => {
    if (a.sportId !== b.sportId) return a.sportId - b.sportId;
    const ga = String(a.groupLabel || '');
    const gb = String(b.groupLabel || '');
    if (ga !== gb) return ga.localeCompare(gb);
    return (a.matches[0]?.league?.name || '').localeCompare(
      b.matches[0]?.league?.name || ''
    );
  });
  const out: Cluster[] = [];
  for (const b of sorted) {
    const gl =
      b.groupLabel != null && String(b.groupLabel).trim() !== ''
        ? String(b.groupLabel).trim()
        : null;
    const last = out[out.length - 1];
    if (
      last &&
      last.sportId === b.sportId &&
      (last.groupLabel || '') === (gl || '')
    ) {
      last.blocks.push(b);
    } else {
      out.push({ sportId: b.sportId, groupLabel: gl, blocks: [b] });
    }
  }
  return out;
}

function groupBlocksBySport(blocks: ResultsLeagueBlock[]) {
  const m = new Map<
    number,
    { leagueId: number; leagueName: string; matches: MatchOffer[] }[]
  >();
  for (const b of blocks) {
    const sid = b.sportId;
    const row = {
      leagueId: b.leagueId,
      leagueName:
        b.matches[0]?.league?.display_name || b.matches[0]?.league?.name || '',
      matches: b.matches,
    };
    const arr = m.get(sid) ?? [];
    arr.push(row);
    m.set(sid, arr);
  }
  return m;
}

export const ResultsMatchTable = ({
  blocks,
  searchMode = false,
  showDayTabs = false,
  feedBlocksByTab,
}: {
  blocks: ResultsLeagueBlock[];
  searchMode?: boolean;
  showDayTabs?: boolean;
  feedBlocksByTab?: {
    today: ResultsLeagueBlock[];
    yesterday: ResultsLeagueBlock[];
  };
}) => {
  const { t, i18n } = useTranslation();
  const { deselectLeague, deselectLeagues } = useContext(LeagueContext);
  const [tab, setTab] = useState<TabKey>('today');

  const { todayFrom, todayTo, yFrom, yTo } = useMemo(() => {
    const now = new Date();
    const todayStart = startOfLocalDay(now);
    const todayEnd = new Date(todayStart);
    todayEnd.setHours(23, 59, 59, 999);
    const yStart = startOfLocalDay(addDays(now, -1));
    const yEnd = new Date(yStart);
    yEnd.setHours(23, 59, 59, 999);
    return {
      todayFrom: todayStart.getTime(),
      todayTo: todayEnd.getTime(),
      yFrom: yStart.getTime(),
      yTo: yEnd.getTime(),
    };
  }, []);

  const tabbedBlocksFromProps = useMemo(() => {
    const from = tab === 'today' ? todayFrom : yFrom;
    const to = tab === 'today' ? todayTo : yTo;
    return blocks
      .map((b) => ({
        ...b,
        matches: b.matches.filter((m) => isUnixInRange(m.time, from, to)),
      }))
      .filter((b) => b.matches.length > 0);
  }, [blocks, tab, todayFrom, todayTo, yFrom, yTo]);

  const activeBlocks = useMemo(() => {
    if (showDayTabs && feedBlocksByTab) {
      return tab === 'today'
        ? feedBlocksByTab.today
        : feedBlocksByTab.yesterday;
    }
    if (showDayTabs) {
      return tabbedBlocksFromProps;
    }
    return blocks;
  }, [showDayTabs, feedBlocksByTab, tab, tabbedBlocksFromProps, blocks]);

  const leagueClusterMode = !showDayTabs && !searchMode;

  // The filter bar narrows whatever this table shows (a day tab or the
  // selected leagues' last weeks).
  const [filters, setFilters] = useState<ResultsFilters>(NO_RESULTS_FILTERS);
  const totalMatches = activeBlocks.reduce((n, b) => n + b.matches.length, 0);
  const dayOptions = useMemo((): [string, string][] => {
    if (showDayTabs) return [];
    const keys = new Set<string>();
    activeBlocks.forEach((b) =>
      b.matches.forEach((m) => keys.add(localDayKey(m.time)))
    );
    return Array.from(keys)
      .sort()
      .reverse()
      .map((k) => [
        k,
        formatResultsDayHeading(new Date(`${k}T12:00:00`), i18n.language),
      ]);
  }, [activeBlocks, showDayTabs, i18n.language]);
  const visibleBlocks = useMemo(
    () => filterResultsBlocks(activeBlocks, filters),
    [activeBlocks, filters]
  );
  const shownMatches = visibleBlocks.reduce((n, b) => n + b.matches.length, 0);

  const clusters = useMemo(
    () => (leagueClusterMode ? clusterBySportAndGroup(visibleBlocks) : null),
    [leagueClusterMode, visibleBlocks]
  );

  const bySport = useMemo(
    () => (!leagueClusterMode ? groupBlocksBySport(visibleBlocks) : null),
    [leagueClusterMode, visibleBlocks]
  );

  const sportIdsSorted = useMemo(() => {
    if (bySport) {
      return Array.from(bySport.keys()).sort((a, b) => {
        const na = SPORT_NAMES_MAP.get(a as any) ?? String(a);
        const nb = SPORT_NAMES_MAP.get(b as any) ?? String(b);
        return na.localeCompare(nb);
      });
    }
    return [];
  }, [bySport]);

  const hasAnyInTab = visibleBlocks.some((b) => b.matches.length > 0);

  const renderLeagueBlock = (
    block: ResultsLeagueBlock,
    sportId: number,
    opts: { showLeagueRemove: boolean }
  ) => (
    <div key={`${block.leagueId}-${sportId}`} className={styles.leagueBlock}>
      <div className={styles.leagueHeadRow}>
        <div className={styles.leagueTitle}>
          {block.matches[0]?.league?.display_name ||
            block.matches[0]?.league?.name ||
            ''}
        </div>
        {opts.showLeagueRemove && (
          <ThemeIcon
            icon="remove"
            alt=""
            variant="inline"
            className={styles.removeLeague}
            onClick={() => deselectLeague(block.leagueId)}
          />
        )}
      </div>

      <div className={styles.headerGrid}>
        <div />
        <div />
        <div />
        <div className={styles.outcome}>
          <div>{t('results_col_ft')}</div>
          <div>{t('results_col_ht')}</div>
          <div className={styles.wide}>{t('results_col_ot')}</div>
          <div className={styles.wide}>{t('results_col_tip')}</div>
          <div>{t('results_col_odd')}</div>
        </div>
      </div>

      {groupMatchesByLocalDay(block.matches, i18n.language).map((dayGroup) => (
        <div key={dayGroup.dayKey}>
          <div className={styles.emDate}>{dayGroup.label}</div>
          <div className={styles.rowsBody}>
            {dayGroup.matches.map((match) => {
              const kick = new Date(match.time * 1000);
              const cols = buildResultsRowDisplay(match, sportId);
              return (
                <ResultRow
                  key={match.fi}
                  match={match}
                  kick={kick}
                  cols={cols}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const renderCountryTitle = (sportId: number, groupLabel: string | null) => {
    const sportLabel = t(sportTranslationKey(sportId), {
      defaultValue: SPORT_NAMES_MAP.get(sportId as any) ?? `Sport ${sportId}`,
    });
    const g = groupLabel && String(groupLabel).trim();
    return g ? `${sportLabel} — ${g}` : sportLabel;
  };

  return (
    <div className={styles.wrap}>
      {showDayTabs && (
        <div className={styles.tabs}>
          <button
            type="button"
            className={tab === 'today' ? styles.tabActive : styles.tab}
            onClick={() => setTab('today')}
          >
            {t('results_tab_today')}
          </button>
          <button
            type="button"
            className={tab === 'yesterday' ? styles.tabActive : styles.tab}
            onClick={() => setTab('yesterday')}
          >
            {t('results_tab_yesterday')}
          </button>
        </div>
      )}

      {totalMatches > 0 && (
        <ResultsFilterBar
          value={filters}
          onChange={setFilters}
          days={dayOptions}
          shown={shownMatches}
          total={totalMatches}
        />
      )}

      {!hasAnyInTab ? (
        <div className={styles.emptyDay}>{t('results_tab_empty')}</div>
      ) : leagueClusterMode && clusters ? (
        clusters.map((cl) => {
          const ids = cl.blocks.map((b) => b.leagueId);
          return (
            <div
              key={`${cl.sportId}-${cl.groupLabel || ''}`}
              className={styles.sportBlock}
            >
              <div className={styles.countryResult}>
                <div className={styles.sportHead}>
                  <div className={styles.sportHeadMain}>
                    <SportIcon
                      sportId={cl.sportId}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultSportIconSrc();
                      }}
                      alt=""
                    />
                    <span>{renderCountryTitle(cl.sportId, cl.groupLabel)}</span>
                  </div>
                  <ThemeIcon
                    icon="remove"
                    alt=""
                    variant="inline"
                    className={styles.countryClose}
                    onClick={() => deselectLeagues(ids)}
                  />
                </div>
                {cl.blocks.map((b) =>
                  renderLeagueBlock(b, cl.sportId, { showLeagueRemove: false })
                )}
              </div>
            </div>
          );
        })
      ) : showDayTabs ? (
        visibleBlocks.map((block) => (
          <div
            key={`${block.sportId}-${block.leagueId}-${String(block.groupLabel)}`}
            className={styles.sportBlock}
          >
            <div className={styles.countryResult}>
              <div className={styles.sportHead}>
                <div className={styles.sportHeadMain}>
                  <SportIcon
                    sportId={block.sportId}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getDefaultSportIconSrc();
                    }}
                    alt=""
                  />
                  <span>
                    {renderCountryTitle(
                      block.sportId,
                      block.groupLabel ?? null
                    )}
                  </span>
                </div>
              </div>
              {renderLeagueBlock(block, block.sportId, {
                showLeagueRemove: false,
              })}
            </div>
          </div>
        ))
      ) : (
        sportIdsSorted.map((sportId) => {
          const leagues = bySport!.get(sportId)!;
          return (
            <div key={sportId} className={styles.sportBlock}>
              <div className={styles.countryResult}>
                <div className={styles.sportHead}>
                  <div className={styles.sportHeadMain}>
                    <SportIcon
                      sportId={sportId}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultSportIconSrc();
                      }}
                      alt=""
                    />
                    <span>{renderCountryTitle(sportId, null)}</span>
                  </div>
                </div>

                {leagues.map((lg) => {
                  const block: ResultsLeagueBlock = {
                    matches: lg.matches,
                    leagueId: lg.leagueId,
                    sportId,
                  };
                  return renderLeagueBlock(block, sportId, {
                    showLeagueRemove: !searchMode,
                  });
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

/** One finished match; it opens the match's statistics and timeline. */
const ResultRow = ({
  match,
  kick,
  cols,
}: {
  match: MatchOffer;
  kick: Date;
  cols: ReturnType<typeof buildResultsRowDisplay>;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className={classNames(styles.row, styles.rowClickable)}
        role="button"
        tabIndex={0}
        title={t('match_statistics_label')}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
      >
        <div className={styles.timeCell}>
          {formatDateTime(kick, {
            removeDate: true,
            removeSeconds: true,
          })}
        </div>
        <div className={styles.teams}>
          <span>{match.home?.name ?? ''}</span>
          <span>{match.away?.name ?? ''}</span>
        </div>
        <div className={styles.noStats}>
          <ThemeIcon
            icon="stats-on"
            alt={t('match_statistics_label')}
            variant="inline"
          />
        </div>
        <div className={styles.outcome}>
          <div className={styles.ftScore}>{cols.ft}</div>
          <div>{cols.ht}</div>
          <div className={styles.wide}>{cols.ot}</div>
          <div className={styles.wide}>{cols.tip}</div>
          <div>{cols.odd}</div>
        </div>
      </div>
      {open && (
        <MatchInsightsModal fi={match.fi} onClose={() => setOpen(false)} />
      )}
    </>
  );
};
